'use client';

/**
 * PublishMenu — custom inline dropdown for the "Publish" split-button in
 * CreatePostModal.
 *
 * Why this is a custom component instead of Radix Popover/DropdownMenu:
 *   - This branch has already hit the Radix Dialog + Radix Popover/Select
 *     pointer-events bug on React 19 twice. Portaled floating UI inside a
 *     Radix Dialog silently breaks pointer events and/or swallows clicks.
 *   - We follow the same pattern as `components/ui/simple-select.tsx` and
 *     `components/tiptap-ui/block-insert-menu/block-insert-menu.tsx`:
 *     render the menu inline (no portal), absolutely positioned relative to
 *     a `relative` wrapper, closing on outside mousedown and Escape.
 *
 * This component is intentionally generic: it only knows how to render a
 * menu of actions and emit `onSelect`. The consumer decides what each
 * action does.
 */

import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Calendar, FileText } from 'lucide-react';

export type PublishActionId = 'schedule' | 'draft';

interface PublishMenuProps {
  /** The chevron trigger element (passed as children). */
  children: ReactNode;
  /** Called when the user picks an action. The consumer decides what to do
   *  (open a date picker for "schedule", call the draft action for "draft"). */
  onSelect: (action: PublishActionId) => void;
  /** Disable the whole menu (mirrors the parent button's disabled state). */
  disabled?: boolean;
}

interface PublishMenuAction {
  id: PublishActionId;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
}

const ACTIONS: PublishMenuAction[] = [
  { id: 'schedule', label: 'Schedule', Icon: Calendar },
  { id: 'draft', label: 'Save as draft', Icon: FileText },
];

function PublishMenuImpl({
  children,
  onSelect,
  disabled = false,
}: PublishMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside mousedown
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape — stopPropagation so a parent Radix Dialog doesn't also
  // close as a side-effect.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    setOpen((prev) => !prev);
  }, [disabled]);

  const handleSelect = useCallback(
    (action: PublishActionId) => {
      onSelect(action);
      setOpen(false);
    },
    [onSelect],
  );

  return (
    <div ref={containerRef} className="relative inline-block">
      <div onClick={handleToggle}>{children}</div>

      {open && (
        <div
          role="menu"
          className="absolute bottom-full right-0 z-50 mb-2 min-w-[180px] rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg"
        >
          {ACTIONS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              role="menuitem"
              onClick={() => handleSelect(id)}
              className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent"
            >
              <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const PublishMenu = memo(PublishMenuImpl);
export default PublishMenu;
